import { useCallback, useEffect, useMemo, useState } from 'react';
import { Accordion } from '../accordion/Accordion';
import { HistoryCalendarView, HistoryCalendarValue, HistoryCalendarMode } from './HistoryCalendarView';

export type HistoryPickerValue =
	| null
	| { dateType: 'default'; date: Date }
	| { dateType: 'range'; start: Date | null; end: Date | null }
	| { dateType: 'year'; start: Date; end: Date };

export type HistoryPickerProps = {
	value: HistoryPickerValue;
	onChange: (next: HistoryPickerValue) => void;
	loading?: boolean;
};

function mapPickerValueToCalendar(value: HistoryPickerValue): HistoryCalendarValue {
	if (!value) return null;
	if (value.dateType === 'default') return { mode: 'single', date: value.date };
	if (value.dateType === 'year') return { mode: 'year', start: value.start, end: value.end };
	return { mode: 'range', start: value.start, end: value.end };
}

function mapCalendarToPickerValue(next: HistoryCalendarValue, mode: HistoryCalendarMode): HistoryPickerValue {
	if (!next) return null;

	if (mode === 'single') {
		if (next.mode !== 'single') return null;
		return { dateType: 'default', date: next.date };
	}

	if (mode === 'year') {
		if (next.mode !== 'year') return null;
		return { dateType: 'year', start: next.start, end: next.end };
	}

	if (next.mode !== 'range') return null;
	return { dateType: 'range', start: next.start, end: next.end };
}

function inferMode(value: HistoryPickerValue): HistoryCalendarMode {
	if (!value) return 'single';
	if (value.dateType === 'range') return 'range';
	if (value.dateType === 'year') return 'year';
	return 'single';
}

export function HistoryPicker({ value, onChange, loading = false }: HistoryPickerProps) {
	const currentMode = useMemo(() => inferMode(value), [value]);

	const [openPanel, setOpenPanel] = useState<'single' | 'range' | 'year'>(() => {
		if (currentMode === 'range') return 'range';
		if (currentMode === 'year') return 'year';
		return 'single';
	});

	useEffect(() => {
		if (currentMode === 'range') setOpenPanel('range');
		else if (currentMode === 'year') setOpenPanel('year');
		else setOpenPanel('single');
	}, [currentMode]);

	const calendarValue = useMemo(() => mapPickerValueToCalendar(value), [value]);

	const setMode = useCallback(
		(mode: 'single' | 'range' | 'year') => {
			setOpenPanel(mode);

			if (mode === 'single') {
				if (value?.dateType === 'default') return;

				let fallbackDate: Date | null = null;
				if (value?.dateType === 'range') fallbackDate = value.start;
				else if (value?.dateType === 'year') fallbackDate = value.start;

				if (fallbackDate) {
					onChange({ dateType: 'default', date: fallbackDate });
					return;
				}

				onChange(null);
				return;
			}

			if (mode === 'range') {
				if (value?.dateType === 'range') return;

				if (value?.dateType === 'default') {
					onChange({ dateType: 'range', start: value.date, end: value.date });
					return;
				}

				if (value?.dateType === 'year') {
					onChange({ dateType: 'range', start: value.start, end: value.end });
					return;
				}

				onChange({ dateType: 'range', start: null, end: null });
				return;
			}

			// year mode
			if (value?.dateType === 'year') return;

			let referenceDate: Date;
			if (value?.dateType === 'default') referenceDate = value.date;
			else if (value?.dateType === 'range' && value.start) referenceDate = value.start;
			else referenceDate = new Date();

			const year = referenceDate.getFullYear();
			// eslint-disable-next-line no-magic-numbers
			onChange({ dateType: 'year', start: new Date(year, 0, 1), end: new Date(year, 11, 31) });
		},
		[onChange, value]
	);

	const onSingleChange = useCallback(
		(next: HistoryCalendarValue) => onChange(mapCalendarToPickerValue(next, 'single')),
		[onChange]
	);

	const onRangeChange = useCallback(
		(next: HistoryCalendarValue) => onChange(mapCalendarToPickerValue(next, 'range')),
		[onChange]
	);

	const onYearChange = useCallback(
		(next: HistoryCalendarValue) => onChange(mapCalendarToPickerValue(next, 'year')),
		[onChange]
	);

	const singleOpen = openPanel === 'single';
	const rangeOpen = openPanel === 'range';
	const yearOpen = openPanel === 'year';

	return (
		<div>
			<Accordion
				title="Select a date"
				radioButton
				hideChevron
				isOpen={singleOpen}
				radioButtonChecked={singleOpen}
				onRadioButtonChange={() => setMode('single')}
				onToggle={(isOpen) => setMode(isOpen ? 'single' : 'range')}
				size="large"
			>
				<HistoryCalendarView
					open
					mode="single"
					value={calendarValue}
					onChange={onSingleChange}
					loading={loading}
					pickerProps={{
						numberOfMonths: 1,
						weekStartDayIndex: 1,
						showOtherDays: true,
						highlightToday: true,
					}}
				/>
			</Accordion>

			<div style={{ height: 10 }} />

			<Accordion
				title="Select date range"
				radioButton
				hideChevron
				isOpen={rangeOpen}
				radioButtonChecked={rangeOpen}
				onRadioButtonChange={() => setMode('range')}
				onToggle={(isOpen) => setMode(isOpen ? 'range' : 'single')}
				size="large"
			>
				<HistoryCalendarView
					open
					mode="range"
					value={calendarValue}
					onChange={onRangeChange}
					loading={loading}
					pickerProps={{
						numberOfMonths: 1,
						weekStartDayIndex: 1,
						showOtherDays: true,
						highlightToday: true,
						rangeHover: true,
					}}
				/>
			</Accordion>

			<div style={{ height: 10 }} />

			<Accordion
				title="Select year"
				radioButton
				hideChevron
				isOpen={yearOpen}
				radioButtonChecked={yearOpen}
				onRadioButtonChange={() => setMode('year')}
				onToggle={(isOpen) => setMode(isOpen ? 'year' : 'single')}
				size="large"
			>
				<HistoryCalendarView
					open
					mode="year"
					value={calendarValue}
					onChange={onYearChange}
					loading={loading}
				/>
			</Accordion>
		</div>
	);
}
